import { BaseModel } from '../base.model';
import {
  EvaluatedLevel,
  PsRisk,
  PsSignalSet,
  PsSupport,
  PublicServiceMeta,
  ServiceStatus,
} from '.';
import { JsonObject } from '@/lib/helpers';

export class PublicServiceModel extends BaseModel {
  type: string;
  name: string;
  description: string;
  image: string;
  hosts: string[];
  unauthenticatedHosts: string[];
  website: string;
  companyName: {
    value: string;
    excerpts: string[];
  };
  support: PsSupport;
  signals: PsSignalSet;
  risk: PsRisk;

  constructor(props: JsonObject = {}) {
    super(props);

    const data = props as Record<keyof PublicServiceModel, unknown>;

    this.type = (data.type as string) || '';
    this.name = (data.name as string) || '';
    this.description = (data.description as string) || '';
    this.image = (data.image as string) || '';
    this.hosts = (data.hosts as string[]) || [];
    this.unauthenticatedHosts = (data.unauthenticatedHosts as string[]) || [];
    this.website = (data.website as string) || '';
    this.companyName = (data.companyName as { value: string; excerpts: string[] }) || {
      value: '',
      excerpts: [],
    };

    this.support = (data.support as PsSupport) || {
      level: 'access',
      state: { disabled: false, code: 0 },
      serviceState: 'enabled',
      stateCode: 0,
    };

    this.signals = (data.signals as PsSignalSet) || {
      companyCountry: {
        value: '',
        excerpts: [],
        description: '',
      },
      trainOnUserData: {
        value: '',
        excerpts: [],
        description: '',
      },
      noModeration: {
        value: '',
        excerpts: [],
        description: '',
      },
      confidence: 0,
    };

    this.risk = (data.risk as PsRisk) || {
      base: { level: 'LOW', score: 0 },
      weighted: { level: 'LOW', score: 0 },
    };
  }

  get riskLevel(): EvaluatedLevel {
    return this.risk.base.level.toLowerCase() as EvaluatedLevel;
  }

  get isDeprecated(): boolean {
    return !!this.support.deprecated?.value;
  }

  get status(): ServiceStatus {
    if (this.isDeprecated) {
      return 'deprecated';
    }

    const serviceState = this.support.serviceState;

    if (
      serviceState === 'passthrough-before-access-control' ||
      serviceState === 'passthrough-before-auth' ||
      serviceState === 'passthrough-before-data-handling'
    ) {
      return 'degraded';
    }

    if (serviceState === 'enabled') {
      return 'available';
    }

    if (serviceState === 'disabled') {
      return 'inactive';
    }

    return serviceState || 'available';
  }

  get eolTimestamp(): number {
    return this.support.deprecated?.eolTimestamp || 0;
  }

  // @todo there's gotta be a better way to do this
  // Matches on SurePath AI and the SDK
  get isSurePath(): boolean {
    return String(this.name).toLowerCase().includes('surepath');
  }

  get meta(): PublicServiceMeta {
    return {
      id: this.id || '',
      name: this.name,
      image: this.image,
      description: this.description,
      risk: this.risk,
      riskLevel: this.riskLevel,
      isSurePath: this.isSurePath,
      website: this.website,
    };
  }
}
